import { useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useCan } from "@/auth/permissions";
import { NotFoundPage } from "@/pages/NotFoundPage";
import {
  dataTime, marketingApi, MKT_QK, money, openS4Console, showNum, useMarketingStatus,
  type MarketingPost, type MarketingPosts, type PostStatus, type S4Meta,
} from "./api";
import { SocialProductsTable } from "./SocialProductsTable";

/**
 * MKT-1 · 内容营销（个人站订阅版一级模块 · 路由 /marketing）。
 * 样稿：designs/mockups/theme/个人站订阅版-样稿.html 画面 ④（tag saas-mockup-v1）——页签：社媒概览 / 帖子列表 / 产品开放 + 右上「进入 S4 控制台 ↗」。
 * 写帖、排期、选平台都在 S4 里做；这里只放概览、帖子列表、产品开放开关。
 * 🔴 企业版 / 未开通：整页 404（菜单本来就不显示）；S4 拿不到的数字一律「未知」，不显示 0。
 * 接口：见同目录 API_MAP.md。
 */
type Tab = "overview" | "posts" | "products";
const TABS: { key: Tab; label: string }[] = [
  { key: "overview", label: "社媒概览" },
  { key: "posts", label: "帖子列表" },
  { key: "products", label: "产品开放" },
];
const card = "rounded-md border border-slate-200 bg-white";

export function MarketingPage() {
  const statusQ = useMarketingStatus();
  const [sp, setSp] = useSearchParams();
  const raw = sp.get("tab");
  const tab: Tab = raw === "posts" || raw === "products" ? raw : "overview";
  const canEnter = useCan("marketing.s4.enter");
  const [enterErr, setEnterErr] = useState<string | null>(null);
  const [entering, setEntering] = useState(false);

  if (statusQ.isLoading) return <div className="p-8 text-center text-[13px] text-slate-500">加载中…</div>;
  if (!statusQ.data?.enabled) return <NotFoundPage />;
  const st = statusQ.data;

  const enter = async () => {
    setEntering(true);
    setEnterErr(await openS4Console());
    setEntering(false);
  };

  return (
    <div className="flex flex-col gap-3.5 p-6 lg:p-8" data-testid="marketing-page">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-bold text-slate-900">内容营销</h1>
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11.5px] font-medium text-emerald-700" data-testid="mkt-pill">已开通 · 生态 S4</span>
        <span className="flex-1" />
        {canEnter && st.s4_configured && (
          <button type="button" data-testid="mkt-enter-s4" disabled={entering} onClick={enter}
            className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-[13px] font-medium text-white hover:bg-brand-hover disabled:opacity-60">
            进入 S4 控制台 <ExternalLink size={13} />
          </button>
        )}
      </div>
      {!st.s4_configured && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800" data-testid="mkt-s4-off">
          {st.s4_label ?? "社媒服务未开通"}：发帖数、帖子列表暂时拿不到（显示「未知」）；带来访问 / 订单是本系统自己的数据，照常统计。
        </div>
      )}
      {enterErr && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700" role="alert" data-testid="mkt-enter-err">{enterErr}</div>}

      <div className="flex gap-1 border-b border-slate-200" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} data-testid={`mkt-tab-${t.key}`}
            onClick={() => setSp(t.key === "overview" ? {} : { tab: t.key }, { replace: true })}
            className={`border-b-2 px-4 py-2 text-[14px] font-medium transition ${tab === t.key ? "border-brand font-semibold text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <OverviewKpis />
          <PostsCard compact />
          <ProductsCard />
        </>
      )}
      {tab === "posts" && <PostsCard />}
      {tab === "products" && <ProductsCard />}
    </div>
  );
}

// ── 概览四格 ─────────────────────────────────────────────────────────────
function OverviewKpis() {
  const q = useQuery({ queryKey: MKT_QK.overview("7d"), queryFn: () => marketingApi.overview("7d"), refetchInterval: 300_000 });
  const d = q.data;
  const kpi = "rounded-md border border-slate-200 bg-white px-3 py-2.5";
  if (q.isError) return <button type="button" onClick={() => q.refetch()} className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-left text-[13px] text-rose-700">概览加载失败，点击重试</button>;
  return (
    <>
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4" data-testid="mkt-kpis">
        <div className={kpi}>
          <span className="text-[12px] text-slate-400">本周发帖</span>
          <b className="block text-[20px] text-slate-900" data-testid="kpi-posts">{d ? showNum(d.posts_published) : "…"}</b>
          {d && d.posts_scheduled !== null && (
            <span className="text-[11.5px] text-slate-400" data-testid="kpi-scheduled">待发 {d.posts_scheduled}{d.posts_failed ? ` · 失败 ${d.posts_failed}` : ""}</span>
          )}
        </div>
        <div className={kpi}>
          <span className="text-[12px] text-slate-400">带来访问（落地页）</span>
          <b className="block text-[20px] text-slate-900" data-testid="kpi-visits">{d ? d.visits : "…"}</b>
        </div>
        <div className={kpi}>
          <span className="text-[12px] text-slate-400">带来订单</span>
          <b className="block text-[20px] text-accent" data-testid="kpi-orders">
            {d ? `${d.orders} 笔${d.orders_amount_cents !== null ? ` · ${d.orders_amount_complete ? "" : "≥ "}${money(d.orders_amount_cents)}` : ""}` : "…"}
          </b>
        </div>
        <div className={kpi}>
          <span className="text-[12px] text-slate-400">开放给社媒的产品</span>
          <b className="block text-[20px] text-slate-900" data-testid="kpi-open">{d ? `${d.products_open} / ${d.products_total}` : "…"}</b>
        </div>
      </div>
      <SourceNote s4={d?.s4}>
        发帖数：S4 · 访问 / 订单：本系统按 src=s4 &amp; ref=帖子 统计（近 7 天）· 一期没有按帖浏览 / 互动 · S4 连不上时一律显示「未知」，不显示 0
      </SourceNote>
    </>
  );
}

function SourceNote({ s4, children }: { s4?: S4Meta; children: ReactNode }) {
  const t = dataTime(s4?.generated_at);
  return (
    <div className="text-[11.5px] text-slate-400" data-testid="mkt-src">
      {children}
      {s4 && !s4.ok && s4.label && <span className="ml-2 text-amber-700" data-testid="mkt-s4-label">· S4：{s4.label}</span>}
      {t && <span className="ml-2">· 数据时间 {t}{s4?.stale ? "（最近一次成功的数据）" : ""}</span>}
    </div>
  );
}

// ── 帖子列表 ─────────────────────────────────────────────────────────────
const FILTERS: { key: PostStatus | ""; label: string }[] = [
  { key: "", label: "全部" }, { key: "published", label: "已发" }, { key: "scheduled", label: "待发" }, { key: "failed", label: "失败" }, { key: "draft", label: "草稿" },
];
// 平台显示名 + 色块（样稿 ④）；不认识的平台显示原文、灰色块（S4 契约 §3：不认识的值显示原文，不得报错）
const PLATFORM: Record<string, { label: string; dot: string }> = {
  facebook: { label: "Facebook", dot: "bg-indigo-600" }, instagram: { label: "Instagram", dot: "bg-pink-600" }, tiktok: { label: "TikTok", dot: "bg-slate-900" },
  xiaohongshu: { label: "小红书", dot: "bg-rose-600" }, douyin: { label: "抖音", dot: "bg-slate-900" }, youtube: { label: "YouTube", dot: "bg-red-600" },
};
const STATUS_TONE: Record<string, string> = {
  published: "bg-emerald-50 text-emerald-700", scheduled: "bg-sky-50 text-sky-700", failed: "bg-rose-50 text-rose-700",
  draft: "bg-slate-100 text-slate-600", cancelled: "bg-slate-100 text-slate-500",
};

function PostsCard({ compact }: { compact?: boolean }) {
  const [status, setStatus] = useState<PostStatus | "">("");
  const q = useInfiniteQuery({
    queryKey: MKT_QK.posts(status, null),
    queryFn: ({ pageParam }) => marketingApi.posts(status, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last: MarketingPosts) => last.next_cursor ?? undefined,
  });
  const pages = q.data?.pages ?? [];
  const first = pages[0];
  const items = pages.flatMap((p) => p.items);
  return (
    <div className={card} data-testid="mkt-posts">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2.5">
        <b className="text-slate-900">帖子列表</b>
        <span className="flex-1" />
        {FILTERS.map((f) => (
          <button key={f.key || "all"} type="button" aria-pressed={status === f.key} data-testid={`post-filter-${f.key || "all"}`} onClick={() => setStatus(f.key)}
            className={`rounded-full border px-2.5 py-0.5 text-[12px] ${status === f.key ? "border-brand bg-brand text-white" : "border-slate-200 bg-white text-slate-600"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-50 text-left text-slate-400">
              <th className="h-8 px-2.5 font-medium">产品</th><th className="px-2.5 font-medium">平台</th><th className="px-2.5 font-medium">发布时间</th>
              <th className="px-2.5 font-medium">状态</th><th className="px-2.5 font-medium">带来订单</th><th className="px-2.5" />
            </tr>
          </thead>
          <tbody>
            {q.isLoading && <tr><td colSpan={6} className="py-6 text-center text-slate-400">加载中…</td></tr>}
            {q.isError && <tr><td colSpan={6} className="py-6 text-center text-rose-600">加载失败</td></tr>}
            {first && !first.available && (
              <tr><td colSpan={6} className="py-6 text-center text-slate-500" data-testid="posts-unavailable">帖子列表暂时拿不到：{first.s4.label ?? "未知"}</td></tr>
            )}
            {first?.available && items.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">还没有帖子（0 条）</td></tr>}
            {(compact ? items.slice(0, 6) : items).map((p) => <PostRow key={p.post_id} p={p} />)}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 px-3 py-2">
        <SourceNote s4={first?.s4}>「带来订单」= 本系统按帖子的归因编号统计（全期累计）</SourceNote>
        <span className="flex-1" />
        {!compact && q.hasNextPage && (
          <button type="button" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage} className="rounded-md border border-slate-200 px-3 py-1 text-[12.5px] text-slate-700 hover:border-brand">
            {q.isFetchingNextPage ? "加载中…" : "加载更多"}
          </button>
        )}
      </div>
    </div>
  );
}

function PostRow({ p }: { p: MarketingPost }) {
  const when = p.publish_date ? new Date(p.publish_date).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
  // S4 V1.2 · 「查看效果」：直达 S4 这条帖子的「发布回执与效果」页（权限同进 S4 控制台；S4 里已删的帖子不给按钮）
  const canEnter = useCan("marketing.s4.enter");
  const [perfErr, setPerfErr] = useState<string | null>(null);
  const viewPerf = async () => setPerfErr(await openS4Console(p.post_id));
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid="post-row">
      <td className="h-[34px] whitespace-nowrap px-2.5">
        {p.product_code ? <><b>{p.product_code}</b> {p.product_name}</> : <span className="text-slate-400">{p.product_label ?? p.product_ref ?? "未关联产品"}</span>}
      </td>
      <td className="whitespace-nowrap px-2.5" title={p.channel_name ?? undefined} data-testid="post-platform">
        <span className={`mr-1.5 inline-block h-3 w-3 rounded-sm align-[-1px] ${PLATFORM[p.platform]?.dot ?? "bg-slate-400"}`} />
        {PLATFORM[p.platform]?.label ?? p.platform}
      </td>
      <td className="whitespace-nowrap px-2.5 tabular-nums">{when}</td>
      <td className="whitespace-nowrap px-2.5">
        <span className={`rounded-full px-2 py-0.5 text-[11.5px] font-medium ${STATUS_TONE[p.status] ?? "bg-slate-100 text-slate-600"}`} data-testid="post-status">
          {p.status_label}{p.failure_reason ? `：${p.failure_reason}` : ""}
        </span>
      </td>
      <td className="whitespace-nowrap px-2.5" data-testid="post-orders">
        {/* 样稿 ④：有单显示「N 笔」，没有 / 帖子没挂产品显示「—」；金额放悬停提示（有金额权限才有） */}
        {p.orders ? (
          <span className="font-semibold text-accent" title={p.orders_amount_cents !== null ? `${p.orders_amount_complete ? "" : "≥ "}${money(p.orders_amount_cents)}` : undefined}>{p.orders} 笔</span>
        ) : <span className="text-slate-400">—</span>}
      </td>
      <td className="whitespace-nowrap px-2.5 text-right">
        {p.release_url ? (
          <a href={p.release_url} target="_blank" rel="noopener noreferrer" className="text-[12.5px] text-brand hover:underline">看帖子 ↗</a>
        ) : p.platform === "tiktok" && p.status === "published" ? (
          // S4 10-16 · TikTok 图片帖发布后平台不返回链接（契约 V1 允许 releaseUrl 为空），别让人以为没发出去
          <span className="whitespace-normal text-[12px] text-slate-400" data-testid="post-no-link-tiktok">TikTok 图片帖不提供链接，请在 TikTok App 里查看</span>
        ) : null}
        {canEnter && !p.deleted_in_s4 && (
          <button type="button" onClick={viewPerf} className="ml-2 text-[12.5px] text-brand hover:underline" data-testid="post-view-perf">查看效果 ↗</button>
        )}
        {perfErr && <span className="ml-1.5 text-[11.5px] text-rose-600">{perfErr}</span>}
      </td>
    </tr>
  );
}

// ── 产品开放 ─────────────────────────────────────────────────────────────
function ProductsCard() {
  return (
    <div className={card} data-testid="mkt-products">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2.5">
        <b className="text-slate-900">产品开放</b>
        <span className="text-[12.5px] text-slate-400">哪些产品允许 S4 拿去发帖 · 关掉 = 对 S4 等于下架</span>
      </div>
      <SocialProductsTable />
      <div className="px-3 py-2 text-[11.5px] text-slate-400">只有标了「实拍授权」的图才会被 S4 拿去发帖；没声明的不用（图片库「实拍授权」默认未声明）。</div>
    </div>
  );
}
